import {
  Component, ChangeDetectionStrategy, OnInit, Inject, ElementRef, ViewChild, ChangeDetectorRef, AfterViewInit
} from '@angular/core';
import { Observable, map } from 'rxjs';
import { Router } from '@angular/router';

import { SearchType } from '@search-bar/entities/search-type';
import { Airport } from '@app/modules/core/interfaces/airport';
import { AppFacade } from '@app/app.facade';
import { BaseSearchBarFacade } from '@shared/base-search-bar/base-search-bar-facade';
import { IS_BROWSER } from '@app/modules/core/tokens/app.tokens';
import { ModuleEntity, ModuleEntityGroup } from '@app/entities/modules-country';
import { EVENT_SOURCE } from '@app/modules/core/constants/amplitude.constants';
import { HomeDataResponse } from '@shared/home-sections/entities/home-data.response';
import { HomeFacade } from '@home/home.facade';
import { countryModulesByWidgetsConfig } from '@home/mappers/home.mapper';
import { mapWidgetsByDetail } from '@home-item-detail/mappers/home-item-widgets.mapper';
import { take } from 'rxjs/operators';
import { MobileDeviceService } from '@app/services/mobile-device.service';
import { HomeItemDetailWidgetType } from '@home-item-detail/entities/home-item-detail';
import { ChipActionStatus, ChipActionType } from '@design-system/components/molecules/chip-action/entities/chip-action.types';
import {
  TypographyColor, TypographyFontFamily, TypographyFontSize
} from '@design-system/components/atoms/typography/entities/typography.types';
import { Icon } from '@design-system/components/atoms/icon/entities/icon.types';
import { ButtonIconSize, ButtonIconType } from '@design-system/components/molecules/button-icon/entities/button-icon.types';

@Component({
  selector: 'app-home-search-desktop',
  templateUrl: './home-search-desktop.component.html',
  styleUrls: ['./home-search-desktop.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class HomeSearchDesktopComponent implements OnInit, AfterViewInit {

  @ViewChild('slider') sliderRef: ElementRef;

  public groupSearchTabs = false;

  public switchSelected: SearchType = null;
  public source: string = EVENT_SOURCE.TRAVEL_HOME;

  public showPrev = false;
  public showNext = false;
  public hovered = false;

  public readonly chipActionType = ChipActionType.Long;

  public readonly fontSize = TypographyFontSize;
  public readonly fontFamily = TypographyFontFamily;
  public readonly fontColor = TypographyColor;

  public readonly icon = Icon;
  public readonly buttonIconSize = ButtonIconSize.XL;
  public readonly buttonType = ButtonIconType.Primary;

  constructor(
    private facade: AppFacade,
    private baseSearchBarFacade: BaseSearchBarFacade,
    private readonly homeFacade: HomeFacade,
    private readonly mobileDeviceService: MobileDeviceService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    @Inject(IS_BROWSER) public isBrowser: boolean) { }

  ngOnInit(): void {
    if (this.isBrowser) {
      this.baseSearchBarFacade.resetBranchCode();
      this.baseSearchBarFacade.resetSalesChannel();
      this.baseSearchBarFacade.selectedTabHome$.
        pipe(take(1)).subscribe(searchType => this.switchSelected = searchType).unsubscribe();
    }
  }

  ngAfterViewInit(): void {
    if (this.isBrowser) {
      this.detectChanges();
    }
  }

  get searchType(): typeof SearchType {
    return SearchType;
  }

  get airports$(): Observable<Airport[]> {
    return this.facade.airports$;
  }

  get homeData$(): Observable<HomeDataResponse> {
    return this.homeFacade.homeData$;
  }

  get isMobile$(): Observable<boolean> {
    return this.mobileDeviceService.isMobileDevice$;
  }

  get widgetsByDetail$(): Observable<HomeItemDetailWidgetType[]> {
    return this.homeData$.pipe(
      map(detail => mapWidgetsByDetail(detail))
    );
  }

  get countryModulesCollection$(): Observable<(ModuleEntityGroup | ModuleEntity)[]> {
    return this.widgetsByDetail$.pipe(
      map(widgets => countryModulesByWidgetsConfig(widgets.join(), this.groupSearchTabs))
    );
  }

  public trackById(index: number): number {
    return index;
  }

  public mouseEvent(value: boolean): void {
    this.hovered = value;
    this.cdr.detectChanges();
  }

  public detectChanges(): void {
    this.showLeft();
    this.showRight();
  }

  public scrollToMax(): void {
    this.sliderRef?.nativeElement?.scrollTo?.({ left: this.sliderRef.nativeElement.scrollWidth, behavior: 'smooth' });
  }

  public scrollToMin(): void {
    this.sliderRef?.nativeElement?.scrollTo?.({ left: 0, behavior: 'smooth' });
  }

  public moduleSelected(type: SearchType): boolean {
    return this.switchSelected === type;
  }

  public onClickSwitch(type: SearchType, route: string[]): void {
    this.switchSelected = type;
    if (type === SearchType.HOTELS_DETAIL) {
      this.router.navigate(route, { queryParams: { source: this.source } });
    } else if (type !== SearchType.PACKAGES_HOME) {
      this.baseSearchBarFacade.resetPackageStep();
    }
  }

  public getChipStatus(moduleIsSelected: boolean): ChipActionStatus {
    return moduleIsSelected ? ChipActionStatus.Selected : ChipActionStatus.Default;
  }

  private showLeft(): void {
    this.showPrev = this.sliderRef?.nativeElement?.scrollLeft > 0;
    this.cdr.detectChanges();
  }

  private showRight(): void {
    this.showNext = this.sliderRef?.nativeElement?.scrollLeft + 
      this.sliderRef?.nativeElement?.clientWidth < this.sliderRef?.nativeElement?.scrollWidth;
    this.cdr.detectChanges();
  }
}
